import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DEV-011C · Public customer activation page.
 *
 * Flow:
 *   1. Read `token` from the URL.
 *   2. Verify the invitation (GET /auth/customer/invitation/:token).
 *   3. If valid, show password form; else show generic "not valid"
 *      message with no user-enumeration.
 *   4. Submit password → POST /auth/customer/activate. On success,
 *      call the shared AuthContext refresh path and navigate to
 *      the existing Customer portal (`/dashboard` → My System).
 *
 * The page never receives or displays raw tokens beyond echoing the
 * URL param, and never receives password hashes, invitation ids or
 * DB identifiers.
 */
export default function CustomerActivate() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const { setAuthFromToken } = useAuth() || {};

    const [state, setState] = useState("verifying");   // verifying | ready | invalid | submitting | done
    const [invitation, setInvitation] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");

    const verify = useCallback(async () => {
        setState("verifying");
        setError("");
        if (!token) { setState("invalid"); return; }
        try {
            const resp = await api.verifyCustomerInvitation(token);
            setInvitation(resp);
            setState("ready");
        } catch (e) {
            setInvitation(null);
            setState("invalid");
        }
    }, [token]);

    useEffect(() => { verify(); }, [verify]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (password.length < 12) { setError("Password must be at least 12 characters."); return; }
        if (password !== confirm) { setError("Passwords do not match."); return; }
        setState("submitting");
        try {
            const resp = await api.activateCustomerAccount({ token, password });
            if (setAuthFromToken) {
                await setAuthFromToken(resp.access_token, resp.user);
            }
            toast.success("Welcome! Your account is now active.");
            setState("done");
            navigate("/dashboard", { replace: true });
        } catch (err) {
            const msg = formatApiError(err?.response?.data?.detail) || "We couldn't activate your account.";
            setError(msg);
            setState("ready");
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-4 py-12" data-testid="activate-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#3E9E2E]/15 text-[#3E9E2E] flex items-center justify-center mx-auto">
                        <ShieldCheck size={20} strokeWidth={1.75} />
                    </div>
                    <h1 className="mt-3 text-2xl font-semibold text-[#131B2F]">Activate your account</h1>
                    <p className="text-sm text-[#4B5565] mt-1">Set a password to access your Pearl Energy system.</p>
                </div>

                {state === "verifying" && (
                    <div className="pe-card p-8 text-center" data-testid="activate-verifying">
                        <Loader2 className="animate-spin mx-auto" size={18} />
                        <div className="mt-2 text-sm text-[#4B5565]">Checking your invitation…</div>
                    </div>
                )}

                {state === "invalid" && (
                    <div className="pe-card p-8 text-center" data-testid="activate-invalid">
                        <AlertTriangle size={22} className="mx-auto text-[#B45309]" />
                        <div className="mt-3 text-sm font-medium text-[#131B2F]">This activation link is no longer valid.</div>
                        <div className="mt-1 text-xs text-[#4B5565]">
                            If you believe this is a mistake, ask your Pearl Energy contact to send a fresh invitation.
                        </div>
                        <Link to="/login" className="mt-4 inline-flex items-center text-sm text-[#3E9E2E] hover:underline">
                            Back to sign in
                        </Link>
                    </div>
                )}

                {(state === "ready" || state === "submitting") && invitation && (
                    <form onSubmit={submit} className="pe-card p-6" data-testid="activate-form">
                        <div className="text-sm text-[#131B2F]">
                            Setting up sign-in for
                            <div className="font-medium mt-0.5">{invitation.customer_name || invitation.email}</div>
                            <div className="text-xs text-[#4B5565] mt-0.5">{invitation.email}</div>
                        </div>
                        {invitation.project_reference && (
                            <div className="mt-2 text-xs text-[#4B5565]">Project · {invitation.project_reference}</div>
                        )}

                        <label className="block mt-5 text-sm">
                            <span className="text-[11px] uppercase tracking-wide text-[#4B5565]">Password</span>
                            <div className="relative mt-1">
                                <input
                                    type={showPw ? "text" : "password"}
                                    className="pe-input pr-10"
                                    autoFocus
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="activate-password"
                                    minLength={12}
                                    required
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4B5565]"
                                        aria-label={showPw ? "Hide password" : "Show password"}>
                                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                            </div>
                        </label>

                        <label className="block mt-3 text-sm">
                            <span className="text-[11px] uppercase tracking-wide text-[#4B5565]">Confirm password</span>
                            <input
                                type={showPw ? "text" : "password"}
                                className="pe-input mt-1"
                                autoComplete="new-password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="activate-confirm"
                                required
                            />
                        </label>

                        <p className="mt-2 text-[11px] text-[#4B5565]">
                            Use at least 12 characters. Avoid using your name, email or a common password.
                        </p>

                        {error && (
                            <div className="mt-3 text-xs text-[#B91C1C] bg-[#FEF2F2] border border-[#FEE2E2] rounded-md px-3 py-2" data-testid="activate-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={state === "submitting"}
                            className="pe-btn-primary w-full mt-5 h-11"
                            data-testid="activate-submit"
                        >
                            {state === "submitting"
                                ? <><Loader2 size={14} className="animate-spin" /> Activating…</>
                                : <><CheckCircle2 size={14} /> Activate account</>}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
